import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import * as Sentry from '@sentry/browser';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';

import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../hooks/useSessionStore';
import LoadingPage from '../../../../../components/LoadingPage';
import ContractHistory from '../../../../../components/ContractHistory';
import User from '../../../../../Model/User';
import UserContract from '../../../../../Model/UserContract';
import Subscription from '../../../../../Model/Subscription';
import { FUTURE_CONTRACT_STATUSES, ContractStatus } from '../../../../utils/userContract';
import { buildBillingTargets, listedTargets, productLabel } from '../../../../utils/billingTarget';
import CollectionName from '../../../../../utils/collections';
import { openFirestoreDocument } from '../../../../../utils/support';
import ReonboardingContractModal from '../../../ContractModal/Modals/ReonboardingContractModal';

import {
  Container,
  NoAccountMessage,
  StyledContractDetails,
  StyledOnCallActions,
} from './styles';
import texts from './texts.json';
import BillingSummary from './BillingSummary';

const SCHEDULED_AMENDMENT_STATUSES = [
  ContractStatus.AMENDMENT_ACCEPTED,
  ContractStatus.RETENTION_PENDING_ACTIVATION,
];

/*
  A pending contract blocks new terms on the product it is for, not on the client: one product
  waiting on a signature must not stop another being amended. An amendment also blocks the
  contract it replaces, because it may switch product - keying on the product alone would let
  the same contract be amended twice. A future contract naming neither cannot be placed, so it
  falls back to blocking them all.
*/
const buildFutureContractBlocker = (contractDocs) => {
  const future = contractDocs.filter((contract) => FUTURE_CONTRACT_STATUSES.includes(contract.status));
  if (!future.length) {
    return () => false;
  }
  if (future.some((contract) => !contract.originalContract && !contract.productId)) {
    return () => true;
  }
  const blockedContractIds = new Set(future.map((contract) => contract.originalContract).filter(Boolean));
  const blockedProductIds = new Set(future.map((contract) => contract.productId).filter(Boolean));
  return (contractId, productId) => blockedContractIds.has(contractId) || blockedProductIds.has(productId);
};

const BillingDetails = ({ userDoc }) => {
  const { coachDoc, externalCoachDoc } = useContext(ExternalCoachContext);
  const pageCoachId = coachDoc?.id;
  const pageStripeAccountId = externalCoachDoc?.stripeAccountId;
  const [userContractsCollection, setUserContractsCollection] = useState(null);
  const [subscriptionsCollection, setSubscriptionsCollection] = useState(null);
  const [contractModalState, setContractModalState] = useState({
    isOpen: false,
    isAmendment: false,
    contractId: '',
    productId: '',
  });
  const [isReady, setIsReady] = useState(false);
  const { isOnCallUser } = useSessionStore();
  const isComponentMountedRef = useComponentMounted();

  // The contract is named because the modal stamps it as the amendment's `originalContract`,
  // which the server follows to decide which subscription to end.
  // `productId` scopes which existing draft may prefill the form: a draft belonging to another
  // product must never be loaded over the product the coach actually chose.
  const openContractModal = useCallback((isAmendment, contractId = '', productId = '') => {
    setContractModalState({
      isOpen: true, isAmendment, contractId, productId,
    });
  }, []);

  const closeContractModal = useCallback(() => {
    setContractModalState({
      isOpen: false, isAmendment: false, contractId: '', productId: '',
    });
  }, []);

  useEffect(() => {
    const init = async () => {
      setUserContractsCollection(null);
      setSubscriptionsCollection(null);
      setIsReady(false);

      if (!pageCoachId) {
        setIsReady(true);
        return;
      }
      const { id: userId } = userDoc;
      const [contractsCollection, subsCollection] = await Promise.all([
        UserContract.getContractsByUserAndCoach(userId, pageCoachId),
        Subscription.getCurrentSubscriptionsCollectionForUser(userId),
      ]);
      if (isComponentMountedRef.current) {
        setUserContractsCollection(contractsCollection);
        setSubscriptionsCollection(subsCollection);
        setIsReady(true);
      }
    };
    if (!isReady) {
      init();
    }
  }, [
    userDoc,
    isReady,
    isComponentMountedRef,
    pageCoachId,
  ]);

  const contractDocs = useMemo(() => userContractsCollection?.docs || [], [userContractsCollection]);
  /*
    Platform-billed subscriptions carry no account, so filtering on equality would drop them from
    every coach's tab rather than the wrong one's. They stay; the rest are cut to this page.
  */
  const subscriptionDocs = useMemo(
    () => (subscriptionsCollection?.docs || [])
      .filter((doc) => !doc.stripeAccountId || doc.stripeAccountId === pageStripeAccountId),
    [subscriptionsCollection, pageStripeAccountId],
  );

  /*
    Computed in the render body rather than memoised: `userDoc` keeps one identity for the life
    of the screen and `Collection.docs` only changes on membership, so a contract entry changing
    moves neither dep. The component is an `observer`, so reading them here tracks them.
  */
  const billingTargets = buildBillingTargets({
    userDoc,
    contractDocs,
    subscriptionDocs,
  });

  // Rebuilt each render for the same reason `billingTargets` is: a status change moves no dep.
  const isBlockedByFutureContract = buildFutureContractBlocker(contractDocs);

  /*
    A scheduled amendment forces its contract's cancellation to be immediate, so the
    amendment can be invalidated. An amendment raised before the `originalContract` link
    existed names no contract, so it falls back to marking them all.
  */
  const scheduledAmendmentContractIds = useMemo(() => {
    const scheduled = contractDocs.filter((contract) => SCHEDULED_AMENDMENT_STATUSES.includes(contract.status));
    const linked = scheduled.map((contract) => contract.originalContract).filter(Boolean);
    if (linked.length === scheduled.length) {
      return linked;
    }
    return billingTargets.map(({ contractId }) => contractId);
  }, [contractDocs, billingTargets]);

  useEffect(() => {
    if (!isReady || !userContractsCollection || userContractsCollection.hasDocs) {
      return;
    }
    Sentry.captureException(new Error(texts.noContract), {
      extra: {
        userId: userDoc.id,
      },
    });
  }, [isReady, userContractsCollection, userDoc]);

  const subscriptionSupportActions = useMemo(() => {
    const withSubscription = listedTargets(billingTargets, userDoc.contractView?.contractId)
      .filter(({ subscriptionDoc }) => !!subscriptionDoc);
    return withSubscription.map((target) => ({
      onClick: () => {
        openFirestoreDocument(`${CollectionName.INTEGRATIONS}/stripe/subscriptions/${target.subscriptionDoc.id}`);
      },
      disabled: false,
      label: withSubscription.length > 1
        ? format(texts.support.subscriptionDocForProduct, { productName: productLabel(target) })
        : texts.support.subscriptionDoc,
    }));
  }, [billingTargets, userDoc]);

  if (!isReady) {
    return <LoadingPage />;
  }

  if (!pageStripeAccountId) {
    return (
      <Container>
        <NoAccountMessage>{texts.noStripeAccount}</NoAccountMessage>
      </Container>
    );
  }

  return (
    <Container>
      <BillingSummary
        userDoc={userDoc}
        billingTargets={billingTargets}
        isBlockedByFutureContract={isBlockedByFutureContract}
        scheduledAmendmentContractIds={scheduledAmendmentContractIds}
        onOpenContractModal={openContractModal}
      />
      {userContractsCollection?.hasDocs && (
        <StyledContractDetails>
          {isOnCallUser && subscriptionSupportActions.length > 0 && (
            <StyledOnCallActions actions={subscriptionSupportActions} />
          )}
          <ContractHistory
            contractDocs={contractDocs}
            billingTargets={billingTargets}
            onOpenContractModal={openContractModal}
            isBlockedByFutureContract={isBlockedByFutureContract}
          />
        </StyledContractDetails>
      )}
      {!!contractModalState.isOpen && (
        <ReonboardingContractModal
          user={userDoc}
          coachId={userDoc.assignedCoach}
          showModal={contractModalState.isOpen}
          onClose={closeContractModal}
          isAmendment={contractModalState.isAmendment}
          contractId={contractModalState.contractId}
          productId={contractModalState.productId}
        />
      )}
    </Container>
  );
};

BillingDetails.propTypes = {
  userDoc: PropTypes.instanceOf(User),
};

BillingDetails.defaultProps = {
  userDoc: null,
};

export default compose(
  observer,
)(BillingDetails);
